/**
 * The environment a repository describes for itself (§15, §5.7).
 *
 * Values are copied as they are written in the repository — the user reviews and
 * edits the list before the project is saved, and nothing is read outside the
 * folder they pointed at.
 */
import { isDotenvTemplate, parseDotenv } from './env';
import { namesIn, type RepoReader } from './detect-read';

/** One variable as detection found it. Whether it is kept sealed is the caller's. */
export interface DetectedEnvVar {
  key: string;
  value: string;
  isSecret: boolean;
}

/**
 * Dotenv files a developer keeps locally, in the order dotenv layers them — a
 * later file wins. They are gitignored, so the bundle a session is seeded with
 * does not carry them: prefilling from here is the only way a project that
 * needs an environment starts inside the sandbox at all.
 */
const DOTENV_FILES = ['.env', '.env.local', '.env.development', '.env.development.local'];

/** `sk_test_changeme`, `<your-token>`, `xxxx` — a template's stand-in, not a value. */
const PLACEHOLDER = /change[-_]?me|placeholder|your[-_][a-z]|<[^>]*>|x{4,}|^(todo|tbd|\.\.\.|\*+)$/i;

/**
 * Where a JVM project keeps the config Spring, Quarkus and Micronaut read. The
 * files are committed, so like a dotenv template they only ever tell us which
 * variables exist and what they fall back to.
 */
const JVM_CONFIG_DIRS = ['src/main/resources', 'src/main/resources/config', 'config'];
const JVM_CONFIG_FILE = /^application(-[\w.-]+)?\.(ya?ml|properties)$/i;

export async function detectEnv(reader: RepoReader): Promise<DetectedEnvVar[]> {
  const found = new Map<string, string>();
  const fill = (key: string, value: string): void => {
    if (!found.has(key)) found.set(key, PLACEHOLDER.test(value) ? '' : value);
  };

  // Committed sources first — they name the variables and their fallbacks. A
  // local dotenv is the developer's own answer, so it overwrites them.
  for (const file of [...(await namesIn(reader, ''))].filter(isDotenvTemplate).sort()) {
    for (const [key, value] of await readDotenv(reader, file)) fill(key, value);
  }
  for (const [key, value] of await jvmPlaceholders(reader)) fill(key, value);
  for (const rel of DOTENV_FILES) {
    for (const [key, value] of await readDotenv(reader, rel)) found.set(key, value);
  }
  // Every variable, uncapped: a project that needs forty of them needs the
  // fortieth as much as the first, and the list is the user's to trim.
  //
  // None is a secret: whether a value is sealed is the person's choice, made in
  // the list (the owner's ask, 2026-10-02) — a name that reads like a credential
  // used to decide it for them.
  return [...found].map(([key, value]) => ({ key, value, isSecret: false }));
}

/**
 * A JVM project states its environment in `application.yml`, not in a dotenv —
 * so reading dotenv files alone found nothing at all for Spring Boot, and the
 * project was added with an empty environment.
 *
 * Only the placeholders with **no default** are kept: those are the variables
 * the sandbox has to supply. One that carries a default resolves by itself, and
 * copying it out as a variable turned the file's lowest-priority fallback into
 * Spring's highest-priority source — an environment variable outranks every
 * profile, so `application-test.yml` lost to a default read out of
 * `application-prod.yml` (the owner's Java project, 2026-09-30: ~120 of them).
 */
async function jvmPlaceholders(reader: RepoReader): Promise<[string, string][]> {
  const out: [string, string][] = [];
  for (const dir of JVM_CONFIG_DIRS) {
    const files = [...(await namesIn(reader, dir))].filter((file) => JVM_CONFIG_FILE.test(file)).sort();
    for (const file of files) {
      const text = await reader.read(`${dir}/${file}`);
      if (text) out.push(...configPlaceholders(text).filter(([, , fallback]) => fallback === null).map(([key]) => [key, ''] as [string, string]));
    }
  }
  return out;
}

/**
 * `${VAR}` and `${VAR:default}` — what Spring, Quarkus and Micronaut resolve
 * against the environment first: the name, and the default the file carries
 * (`null` when it carries none). UPPER_SNAKE only: `${server.port}` is a
 * reference to another property, not to a variable the sandbox must supply.
 */
export function configPlaceholders(text: string): [string, string, string | null][] {
  return [...text.matchAll(/\$\{([A-Z][A-Z0-9_]*)(?::([^{}]*))?\}/g)].map((match) => [
    match[1],
    (match[2] ?? '').trim(),
    match[2] === undefined ? null : match[2].trim()
  ]);
}

async function readDotenv(reader: RepoReader, rel: string): Promise<[string, string][]> {
  const text = await reader.read(rel);
  return text ? parseDotenv(text) : [];
}

/**
 * `PORT=8088` in a dotenv file. Matched on the key rather than on a `:1234`
 * shape, because the same file holds `DATABASE_URL=…:5432` and taking that as
 * the web port is worse than finding nothing.
 */
export function dotenvPort(env: readonly DetectedEnvVar[]): string | null {
  const found = env.find((one) => /^(?:APP_|SERVER_|HTTP_|WEB_)?PORT$/.test(one.key) && /^\d{2,5}$/.test(one.value));
  return found ? `:${found.value}` : null;
}
